<template>
  <el-dialog
      :model-value="modelValue"
      :title="title"
      :width="responsiveWidth"
      :top="top"
      :append-to-body="true"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      class="guide-carousel-dialog"
      @update:model-value="handleVisibleChange"
  >
    <div v-if="slides.length" class="guide-carousel-wrap">
      <div ref="carouselColRef" class="guide-carousel__col">
        <el-carousel
            ref="carouselRef"
            :height="adaptiveHeight"
            :autoplay="false"
            :loop="false"
            arrow="never"
            :indicator-position="slides.length > 1 ? 'outside' : 'none'"
            trigger="click"
            @change="handleChange"
        >
          <el-carousel-item v-for="(item, index) in slides" :key="index">
            <div
                v-if="item.image"
                class="guide-slide__media"
                @mouseenter="showPreviewHint"
                @mousemove="movePreviewHint"
                @mouseleave="hidePreviewHint"
            >
              <el-image
                  :src="item.image"
                  fit="contain"
                  class="guide-slide__img"
                  @load="(event) => handleImageLoad(index, event)"
                  @dblclick="handlePreview(item.image)"
              >
                <template #error>
                  <div class="guide-slide__img-error">
                    <el-icon>
                      <Picture/>
                    </el-icon>
                  </div>
                </template>
              </el-image>
            </div>
          </el-carousel-item>
        </el-carousel>

        <div v-if="slides.length > 1" class="guide-carousel__overlay">
          <el-tooltip
              :content="reachStartText"
              placement="top"
              :disabled="!isFirst"
          >
            <div
                class="guide-carousel__arrow guide-carousel__arrow--left"
                :class="{ 'is-disabled': isFirst }"
                @click="handlePrev"
            >
              <el-icon>
                <ArrowLeft/>
              </el-icon>
            </div>
          </el-tooltip>

          <el-tooltip
              :content="reachEndText"
              placement="top"
              :disabled="!isLast"
          >
            <div
                class="guide-carousel__arrow guide-carousel__arrow--right"
                :class="{ 'is-disabled': isLast }"
                @click="handleNext"
            >
              <el-icon>
                <ArrowRight/>
              </el-icon>
            </div>
          </el-tooltip>
        </div>
      </div>
    </div>
    <!-- 文字说明：位于轮播图及进度点下方，固定高度避免弹窗高度随内容变化 -->
      <div v-if="activeSlide" class="guide-slide__text">
        <p v-if="activeSlide.title || activeSlide.desc" class="guide-slide__desc">
          <span v-if="activeSlide.title" class="guide-slide__title">{{ activeSlide.title }}{{ $t("common.guide.title.separator") }}</span>
          <span v-if="activeSlideDescParts.main" class="guide-slide__main">{{ activeSlideDescParts.main }}</span>
          <span v-if="activeSlideDescParts.note" class="guide-slide__note">
            <span class="guide-slide__emphasis">{{ $t("common.guide.note.label") }}</span>{{ activeSlideDescParts.note }}
          </span>
        </p>
      </div>
    <!-- 不再弹出：右下角文本按钮 -->
    <div v-if="neverAgainText" class="guide-slide__footer">
      <el-button type="text" class="never-again-btn" @click="handleNeverAgain">{{ neverAgainText }}</el-button>
    </div>
    <!-- 图片预览：改为双击轮播图打开，避免单击误触；hover 时有“双击预览”提示 -->
    <el-image-viewer
        v-if="previewVisible"
        :url-list="previewList"
        teleported
        hide-on-click-modal
        @close="handlePreviewClose"
    />
    <!-- 跟随鼠标位置的预览提示（teleport 到 body，避免被弹窗变换/溢出裁剪） -->
    <Teleport to="body">
      <div
          v-if="previewHint.visible"
          class="guide-preview-hint"
          :style="{ left: `${previewHint.x}px`, top: `${previewHint.y}px` }"
      >{{ $t("common.guide.preview.hint") }}</div>
    </Teleport>
  </el-dialog>
</template>

<script setup>
import {defineProps, defineEmits, ref, reactive, computed, nextTick, onMounted, onBeforeUnmount, watch} from "vue";
import {Picture, ArrowLeft, ArrowRight} from "@element-plus/icons-vue";
import {i18n} from "@/lang";

const props = defineProps({
  // 弹窗显隐，配合 v-model 使用
  modelValue: {
    type: Boolean,
    default: false,
  },
  // 弹窗标题
  title: {
    type: String,
    default: "",
  },
  // 轮播数据：[{ image, title, desc }]
  slides: {
    type: Array,
    default: () => [],
  },
  // 弹窗宽度
  width: {
    type: [String, Number],
    default: "720px",
  },
  // 弹窗距顶部距离（默认较靠上）
  top: {
    type: String,
    default: "2vh",
  },
  // 轮播区域高度
  height: {
    type: String,
    default: "420px",
  },
  // 文字说明区域固定高度（避免弹窗高度随内容变化）
  textHeight: {
    type: String,
    default: "96px",
  },
  // “不再弹出”按钮文案，为空时不展示该按钮
  neverAgainText: {
    type: String,
    default: "",
  },
  // 已到第一张时的悬停提示
  reachStartText: {
    type: String,
    default: () => i18n.global.t("common.guide.reach.start"),
  },
  // 已到最后一张时的悬停提示
  reachEndText: {
    type: String,
    default: () => i18n.global.t("common.guide.reach.end"),
  },
});

const emit = defineEmits(["update:modelValue", "never-again", "close-icon"]);

// 当前轮播下标，用于同步展示对应文字说明
const carouselRef = ref(null);
const carouselColRef = ref(null);
const activeIndex = ref(0);
const carouselWidth = ref(0);
const viewportWidth = ref(typeof window !== "undefined" ? window.innerWidth : 1440);
const viewportHeight = ref(typeof window !== "undefined" ? window.innerHeight : 900);
const imageRatios = ref({});
const handleChange = (index) => {
  activeIndex.value = index;
};
// 图片预览：改为双击触发，避免单击误触；hover 时在鼠标位置展示“双击可预览”提示
const previewVisible = ref(false);
const previewList = ref([]);
const previewHint = reactive({visible: false, x: 0, y: 0});
let previewHintTimer = null;
const clearPreviewHintTimer = () => {
  if (previewHintTimer) {
    clearTimeout(previewHintTimer);
    previewHintTimer = null;
  }
};
const updatePreviewHintPos = (event) => {
  previewHint.x = event.clientX + 12;
  previewHint.y = event.clientY + 16;
};
// 进入图片时短暂提示一次并跟随光标，1.5s 后自动消失，避免一直悬浮干扰；移出后重新进入才会再次提示
const showPreviewHint = (event) => {
  updatePreviewHintPos(event);
  previewHint.visible = true;
  clearPreviewHintTimer();
  previewHintTimer = setTimeout(() => {
    previewHint.visible = false;
  }, 1500);
};
const movePreviewHint = (event) => {
  if (!previewHint.visible) return;
  updatePreviewHintPos(event);
};
const hidePreviewHint = () => {
  clearPreviewHintTimer();
  previewHint.visible = false;
};
const handlePreview = (image) => {
  if (!image) return;
  clearPreviewHintTimer();
  previewHint.visible = false;
  previewList.value = [image];
  previewVisible.value = true;
};
const handlePreviewClose = () => {
  previewVisible.value = false;
};
const activeSlide = computed(() => props.slides?.[activeIndex.value] ?? null);
// 将描述拆分为正文与“注意”提示两部分：注意部分单独换行展示，不使用省略号
// 兼容中英文内容：中文使用“注意：”，英文使用“Note:”作为提示标记
const NOTE_FLAGS = ["注意：", "Note：", "Note:"];
const activeSlideDescParts = computed(() => {
  const desc = activeSlide.value?.desc;
  if (!desc) return {main: "", note: ""};
  const text = String(desc);
  for (const flag of NOTE_FLAGS) {
    const noteIndex = text.indexOf(flag);
    if (noteIndex !== -1) {
      return {
        main: text.slice(0, noteIndex).trim(),
        note: text.slice(noteIndex + flag.length).trim(),
      };
    }
  }
  return {main: text.trim(), note: ""};
});

const getPixelValue = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return null;
  const matched = value.trim().match(/^(\d+(?:\.\d+)?)px$/);
  return matched ? Number(matched[1]) : null;
};

const baseHeightConfig = computed(() => {
  const heightPx = getPixelValue(props.height);
  const baseHeight = heightPx ?? 420;
  const minHeight = Math.max(Math.min(baseHeight + 20, 480), 380);
  const maxHeight = Math.max(baseHeight + 180, 640);
  return {heightPx, minHeight, maxHeight};
});

const fallbackRatio = computed(() => {
  const widthPx = getPixelValue(props.width);
  const heightPx = baseHeightConfig.value.heightPx;
  if (widthPx && heightPx) return widthPx / heightPx;
  return 2.1;
});

// 统一比例：取所有已加载图片中最小的宽高比（即最“高”的一张），
// 使轮播容器高度在切换幻灯片时保持稳定，避免 align-center 反复重新居中导致按钮上下跳动。
// fit="contain" 会让其余图片在固定高度内自适应留白，不会被裁剪。
const stableSlideRatio = computed(() => {
  const ratios = Object.values(imageRatios.value).filter((ratio) => ratio > 0);
  if (!ratios.length) return fallbackRatio.value;
  return Math.min(...ratios);
});
const isCompactViewport = computed(() => viewportWidth.value <= 768);

const dialogHorizontalPadding = computed(() => (isCompactViewport.value ? "4px" : "20px"));
const contentContainerWidth = computed(() => (isCompactViewport.value ? "100%" : `min(100%, ${contentMaxWidth.value})`));
const mediaPadding = computed(() => (isCompactViewport.value ? "0px" : "clamp(6px, 0.8vw, 10px)"));

const contentMaxWidth = computed(() => {
  const widthPx = getPixelValue(props.width);
  if (widthPx === null) return "100%";

  const viewportLimit = Math.max(viewportWidth.value - (isCompactViewport.value ? 20 : 56), 320);
  const finalWidth = Math.min(widthPx, viewportLimit);

  return `${finalWidth}px`;
});

const responsiveWidth = computed(() => {
  const widthPx = getPixelValue(props.width);
  if (widthPx === null) return props.width;
  if (isCompactViewport.value) {
    return `${Math.max(viewportWidth.value - 8, 320)}px`;
  }

  const viewportLimit = Math.max(viewportWidth.value - 24, 320);
  const dialogWidth = Math.min(widthPx, viewportLimit);

  return `${dialogWidth}px`;
});

const adaptiveHeight = computed(() => {
  const {heightPx, minHeight, maxHeight} = baseHeightConfig.value;
  if (heightPx === null) return props.height;
  if (!carouselWidth.value || !stableSlideRatio.value) {
    return `clamp(${minHeight}px, 66vh, ${maxHeight}px)`;
  }

  const idealHeight = carouselWidth.value / stableSlideRatio.value;
  const minRenderableHeight = Math.min(minHeight, Math.max(Math.floor(viewportHeight.value * 0.52), 320));
  const viewportLimit = Math.max(Math.floor(viewportHeight.value * 0.7), minRenderableHeight);
  const finalHeight = Math.min(Math.max(idealHeight, minRenderableHeight), Math.min(maxHeight, viewportLimit));

  return `${Math.round(finalHeight)}px`;
});

const responsiveTextHeight = computed(() => {
  const textHeightPx = getPixelValue(props.textHeight);
  if (textHeightPx === null) return props.textHeight;
  const minHeight = Math.max(Math.min(textHeightPx, 96), 72);
  return `clamp(${minHeight}px, 18vh, ${textHeightPx}px)`;
});

const updateCarouselMetrics = () => {
  carouselWidth.value = carouselColRef.value?.clientWidth || 0;
  if (typeof window !== "undefined") {
    viewportWidth.value = window.innerWidth;
    viewportHeight.value = window.innerHeight;
  }
};

const handleImageLoad = (index, event) => {
  const imageEl = event?.target;
  if (!imageEl?.naturalWidth || !imageEl?.naturalHeight) return;
  imageRatios.value = {
    ...imageRatios.value,
    [index]: imageEl.naturalWidth / imageEl.naturalHeight,
  };
  updateCarouselMetrics();
};

let resizeObserver = null;

onMounted(() => {
  nextTick(() => {
    updateCarouselMetrics();
    if (typeof ResizeObserver !== "undefined" && carouselColRef.value) {
      resizeObserver = new ResizeObserver(() => {
        updateCarouselMetrics();
      });
      resizeObserver.observe(carouselColRef.value);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", updateCarouselMetrics);
    }
  });
});

onBeforeUnmount(() => {
  clearPreviewHintTimer();
  resizeObserver?.disconnect();
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", updateCarouselMetrics);
  }
});

watch(() => props.modelValue, (visible) => {
  if (!visible) {
    clearPreviewHintTimer();
    previewHint.visible = false;
    return;
  }
  // 弹窗重新打开时重置“近期不弹出”标记，避免异常残留导致下次关闭被误判
  closingByNeverAgain = false;
  nextTick(() => {
    updateCarouselMetrics();
  });
});

// 边界判断：首/尾张时对应箭头禁用（保留但不可点击）
const isFirst = computed(() => activeIndex.value <= 0);
const isLast = computed(() => activeIndex.value >= props.slides.length - 1);

// 上一张/下一张：到边界不做处理（由 tooltip 提示）
const handlePrev = () => {
  if (isFirst.value) return;
  carouselRef.value?.prev();
};
const handleNext = () => {
  if (isLast.value) return;
  carouselRef.value?.next();
};

// 标记：本次关闭是否由“近期不弹出”按钮触发（避免同时触发 close-icon 造成 7 天冷却覆盖 90 天冷却）
let closingByNeverAgain = false;

// 点击“不再弹出”：通知父组件持久化（90天冷却），并关闭弹窗
const handleNeverAgain = () => {
  closingByNeverAgain = true;
  emit("never-again");
  emit("update:modelValue", false);
};

// 通过右上角关闭图标关闭弹窗：通知父组件（7天冷却），并同步显隐
// 说明：已禁用点击遮罩/ESC 关闭，el-dialog 仅在点击右上角关闭图标时触发该回调（val=false）
const handleVisibleChange = (val) => {
  if (!val) {
    if (closingByNeverAgain) {
      // 由“近期不弹出”触发的关闭，已单独处理，不再上报 close-icon
      closingByNeverAgain = false;
      emit("update:modelValue", false);
      return;
    }
    emit("close-icon");
  }
  emit("update:modelValue", val);
};
</script>

<style lang="less" scoped>
:deep(.guide-carousel-dialog .el-dialog__header) {
  padding-left: v-bind(dialogHorizontalPadding);
  padding-right: v-bind(dialogHorizontalPadding);
}

:deep(.guide-carousel-dialog .el-dialog__body) {
  padding-left: v-bind(dialogHorizontalPadding);
  padding-right: v-bind(dialogHorizontalPadding);
}

.guide-carousel-wrap {
  width: v-bind(contentContainerWidth);
  margin: 0 auto;
}

.guide-carousel__col {
  position: relative;
  width: 100%;
  min-width: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(235, 238, 245, 0.9);
  background: #fff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
}

.guide-carousel__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  pointer-events: none;
}

.guide-carousel__arrow {
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 56px;
  border-radius: 10px;
  /* 默认呈现浅色圆角长方形底，明确点击区域 */
  background-color: rgba(0, 0, 0, 0.05);
  color: rgba(96, 98, 102, 0.92);
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
  text-shadow: 0 1px 4px rgba(255, 255, 255, 0.65);
  transition: transform 0.2s, opacity 0.2s, color 0.2s, background-color 0.2s;
  user-select: none;
}

.guide-carousel__arrow:hover {
  color: rgba(48, 49, 51, 0.95);
  /* 悬停时底色加深，形状保持圆角长方形 */
  background-color: rgba(0, 0, 0, 0.16);
  transform: scale(1.08);
}

.guide-carousel__arrow.is-disabled {
  color: rgba(144, 147, 153, 0.75);
  opacity: 1;
  cursor: not-allowed;
  transform: none;
}

.guide-carousel__arrow.is-disabled:hover {
  /* 禁用态保持默认浅色底，不加深、不放大 */
  background-color: rgba(0, 0, 0, 0.05);
  transform: none;
}

.guide-slide__media {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: v-bind(mediaPadding);
  box-sizing: border-box;
  background: linear-gradient(180deg, #fff 0%, #f7f8fa 100%);
  border-radius: 6px;
  overflow: hidden;
}

.guide-slide__img {
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.guide-preview-hint {
  position: fixed;
  z-index: 3000;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1;
  color: #fff;
  background: rgba(0, 0, 0, 0.75);
  border-radius: 4px;
  pointer-events: none;
  white-space: nowrap;
}

.guide-slide__img-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #c0c4cc;
  font-size: 40px;
}

.guide-slide__text {
  width: v-bind(contentContainerWidth);
  height: v-bind(responsiveTextHeight);
  margin: 20px auto 0;
  padding: 0 12px 8px;
  box-sizing: border-box;
  overflow-y: auto;
}

.guide-slide__title {
  margin-right: 4px;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  line-height: 22px;
}

.guide-slide__desc {
  margin: 0;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 20px;
  word-break: break-word;
  white-space: normal;
  text-align: left;
}

.guide-slide__note {
  display: block;
  margin-top: 4px;
}

.guide-slide__emphasis {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

.guide-slide__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: v-bind(contentContainerWidth);
  margin: 0 auto;
  padding: 8px 12px 4px;
}

.never-again-btn {
  color: #909399;
  font-size: 13px;
  padding: 0;
  
  &:hover {
    color: #606266;
  }
}

@media (max-width: 768px) {
  .guide-carousel__col {
    border-radius: 6px;
  }

  .guide-carousel__overlay {
    padding: 0 2px;
  }

  .guide-carousel__arrow {
    width: 46px;
    height: 32px;
    font-size: 22px;
  }

  .guide-slide__text {
    padding: 4px 0 8px;
  }

  .guide-slide__footer {
    padding: 8px 0 4px;
  }
}
</style>
